// Locale-aware date format maps.
// Keys match BCP 47 language tags stored in workspace.config.locale.
// All formats use moment.js / dayjs token syntax.

export type DateFormats = {
  short: string;
  shortDateTime: string;
  readable: string;
  readableDateTime: string;
  readableDateTimeFilename: string;
  columnHeader: string;
  chartDate: string;
  dayHeader: string;
};

export type SupportedLocale = "en-AU" | "en-GB" | "en-US";

export const DATE_FORMATS: Record<SupportedLocale, DateFormats> = {
  "en-AU": {
    short: "DD/MM/YYYY",
    shortDateTime: "DD/MM/YYYY HH:mm:ss",
    readable: "DD MMM YYYY",
    readableDateTime: "hh:mma DD MMM YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "DD\u00A0MMM\u00A0YYYY",
    /** Short date for chart axes / tooltips \u2014 no year. */
    chartDate: "D MMM",
    /** Day-of-week header for Detail view grouping. */
    dayHeader: "ddd D MMM YYYY",
  },
  "en-GB": {
    short: "DD/MM/YYYY",
    shortDateTime: "DD/MM/YYYY HH:mm:ss",
    readable: "DD MMM YYYY",
    readableDateTime: "hh:mma DD MMM YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "DD\u00A0MMM\u00A0YYYY",
    chartDate: "D MMM",
    dayHeader: "ddd D MMM YYYY",
  },
  "en-US": {
    short: "MM/DD/YYYY",
    shortDateTime: "MM/DD/YYYY HH:mm:ss",
    readable: "MMM DD, YYYY",
    readableDateTime: "hh:mma MMM DD, YYYY",
    readableDateTimeFilename: "YYYYMMDD-HHmm",
    columnHeader: "MMM\u00A0DD,\u00A0YYYY",
    chartDate: "MMM D",
    dayHeader: "ddd MMM D, YYYY",
  },
};

export const DEFAULT_LOCALE: SupportedLocale = "en-AU";

const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  Object.prototype.hasOwnProperty.call(DATE_FORMATS, locale);

export const getDateFormats = (
  locale: string | null | undefined
): DateFormats =>
  (locale && isSupportedLocale(locale) && DATE_FORMATS[locale]) ||
  DATE_FORMATS[DEFAULT_LOCALE];
